import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { useVideoCreatorStore } from '../../stores-v2';
import PlusIconWithRoundBg from '../../svgs/PlusIconWithRoundBg';
import { getThumbnailUrlAndAspectRatio } from '../../utility/videoClip';
import PresetPreviewText from './PresetPreviewText';

import type { Preset } from '../textProcessor/TextBrandingService';

export default function PresetPreview({
  preset: template,
  produceKaraokeText,
}: {
  preset: Preset;
  produceKaraokeText: () => Promise<void>;
}) {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const videoCreator = useVideoCreatorStore();
  const aspectRatio = videoCreator.getCurrentAspectRatio();

  // Derive a thumbnail from current video (if available) to show contextual background.
  const presetBackground = useMemo(() => {
    const currentVideo = videoCreator.currentVideo;
    if (!currentVideo) return undefined;
    const { thumbnailUrl } = getThumbnailUrlAndAspectRatio(
      currentVideo,
      undefined,
      videoCreator.story,
      aspectRatio,
    );
    return thumbnailUrl;
  }, [videoCreator.currentVideo, videoCreator.story, aspectRatio]);

  if (!template) {
    return null;
  }

  const selectPreset = async () => {
    await videoCreator.syncToEditorAtomically(async () => {
      await produceKaraokeText();
    });
    videoCreator.textBrandingService.applyTemplate(
      template,
      'karaoke',
      null,
      undefined,
      false,
    );
    videoCreator.setActiveElements();
  };

  return (
    <PresetWrapper>
      <Preset
        presetBackground={presetBackground}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={selectPreset}
        role="button"
        tabIndex={0}
        aria-label={`Select ${template.title} template`}
      >
        {isHovered ? (
          <>
            <PresetDimOverlay />
            <PresetHoverPreview>
              <PlusIconWithRoundBg width="24" height="24" />
            </PresetHoverPreview>
          </>
        ) : (
          <PresetDimOverlayLight />
        )}
        <VerticalAlignWrapper>
          <PresetPreviewText template={template} />
        </VerticalAlignWrapper>
      </Preset>
      <PresetText>{template.title}</PresetText>
    </PresetWrapper>
  );
}

const PresetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const PresetText = styled.div`
  color: #f3e9d7;
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 159%; /* 15.9px */
`;

const Preset = styled.div<{
  presetBackground?: string;
}>`
  width: 100%;
  max-width: 145px;
  min-width: 0;
  aspect-ratio: 145 / 79;
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
  cursor: pointer;
  z-index: 0;
  ${props =>
    props.presetBackground
      ? css`
          background-image: url(${props.presetBackground});
        `
      : css`
          background-image: url('/img/karaoke-preset-bg.png');
        `}
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #000;
  border-radius: 4px;
  border: 1px solid #484848;

  &:hover {
    border-color: #17c964;
    box-shadow: 0 0 0 1px #17c964;
  }
`;

const PresetHoverPreview = styled.div`
  position: absolute;
  display: flex;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
`;

const PresetDimOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.56);
  border-radius: 4px;
  z-index: 2;
  pointer-events: none;
`;

const PresetDimOverlayLight = styled(PresetDimOverlay)`
  background: rgba(0, 0, 0, 0.2);
  z-index: 0;
`;

const VerticalAlignWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  z-index: 1;
`;
